import React, { useState, useEffect } from "react";
import { Play, Clock, Filter, ExternalLink, Eye, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { youtubeService, YouTubeVideo } from "../services/youtubeService";
import PlayfulLoader from "../components/PlayfulLoader";

export const VideosPage: React.FC = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  useEffect(() => {
    const loadVideos = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        // Fetch videos
        const videosData = await youtubeService.getVideos(20); // Get more videos for the videos page

        if (videosData.length > 0) {
          setVideos(videosData);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error("Error loading YouTube videos:", error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadVideos();
  }, []);

  const handleVideoClick = (video: YouTubeVideo) => {
    window.open(video.url, "_blank");
  };

  // Extract categories from video titles and descriptions (improved keyword matching)
  const getVideoCategory = (video: YouTubeVideo): string => {
    const title = video.title.toLowerCase();
    const description = video.description.toLowerCase();
    const combined = `${title} ${description}`;

    if (combined.includes("review") || combined.includes("unboxing")) {
      return "Reviews & Unboxing";
    } else if (
      combined.includes("educational") ||
      combined.includes("learning") ||
      combined.includes("teach")
    ) {
      return "Educational";
    } else if (
      combined.includes("action") ||
      combined.includes("figure") ||
      combined.includes("superhero")
    ) {
      return "Action Figures";
    } else if (
      combined.includes("doll") ||
      combined.includes("princess") ||
      combined.includes("barbie")
    ) {
      return "Dolls & Playsets";
    } else if (
      combined.includes("car") ||
      combined.includes("vehicle") ||
      combined.includes("truck") ||
      combined.includes("race")
    ) {
      return "Vehicles";
    } else if (
      combined.includes("building") ||
      combined.includes("block") ||
      combined.includes("lego") ||
      combined.includes("construction")
    ) {
      return "Building & Construction";
    } else if (
      combined.includes("puzzle") ||
      combined.includes("game") ||
      combined.includes("board")
    ) {
      return "Puzzles & Games";
    } else {
      return "General";
    }
  };

  const getUniqueCategories = () => {
    const categories = new Set<string>();
    videos.forEach((video) => {
      categories.add(getVideoCategory(video));
    });
    return Array.from(categories).sort();
  };

  // Filter videos based on selected category
  const filteredVideos =
    selectedCategory === "all"
      ? videos
      : videos.filter((video) => getVideoCategory(video) === selectedCategory);

  const handleSuggestToy = () => {
    // Navigate to contact page with pre-filled subject
    navigate("/contact", {
      state: {
        prefillSubject: "Toy Suggestion for Video Feature",
      },
    });
  };

  const handleSubscribeClick = () => {
    // Open YouTube channel subscription page
    window.open(
      "https://www.youtube.com/channel/UCIqJtjSzBtfEu-VQeJ6R5Wg?sub_confirmation=1",
      "_blank"
    );
  };

  if (isLoading) {
    return (
      <PlayfulLoader
        size="lg"
        variant="bounce"
        message="🎬 Loading fun videos..."
        fullScreen
      />
    );
  }

  // Don't show the page if there's an error or no videos
  if (hasError || videos.length === 0) {
    return (
      <div className="min-h-screen bg-warm-cream-50 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="text-6xl mb-4">🎥</div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Videos Unavailable
          </h1>
          <p className="text-lg text-gray-600 max-w-md mx-auto mb-6">
            We're unable to load videos at the moment. Please check back later
            or visit our YouTube channel directly.
          </p>
          <a
            href="https://www.youtube.com/channel/UCIqJtjSzBtfEu-VQeJ6R5Wg"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
          >
            <ExternalLink className="h-5 w-5 mr-2" />
            Visit Our YouTube Channel
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-cream-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-soft-purple-100 via-soft-pink-50 to-soft-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-4">🎬</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Toy Videos
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Watch our amazing toy demonstrations, reviews, and fun activities!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Filters */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 text-purple-500 mr-2" />
            <h3 className="text-lg font-bold text-gray-900">
              Filter by Category
            </h3>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                selectedCategory === "all"
                  ? "bg-soft-purple-500 text-white shadow-md"
                  : "bg-white text-gray-700 border border-gray-200 hover:border-soft-purple-300"
              }`}
            >
              All Videos
            </button>

            {getUniqueCategories().map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? "bg-soft-purple-500 text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-soft-purple-300"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Videos Grid */}
        {filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredVideos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                {/* Video Thumbnail */}
                <div className="relative aspect-video bg-gray-200 overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />

                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                    <button
                      onClick={() => handleVideoClick(video)}
                      className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-4 transition-all duration-200 transform hover:scale-110"
                    >
                      <Play className="h-8 w-8 text-red-600 ml-1" />
                    </button>
                  </div>

                  {/* Duration Badge */}
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm font-medium">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {video.duration}
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded-full">
                      YouTube
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(video.publishedAt).toLocaleDateString()}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {video.title}
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {video.description}
                  </p>

                  {/* Video Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <Eye className="h-4 w-4 mr-1" />
                      {video.viewCount} views
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {video.channelTitle}
                      </span>
                    </div>
                  </div>

                  {/* Watch Button */}
                  <button
                    onClick={() => handleVideoClick(video)}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Watch on YouTube
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎥</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No videos in this category
            </h3>
            <p className="text-gray-600 mb-4">
              We're working on adding more {selectedCategory.toLowerCase()}{" "}
              videos!
            </p>
            <button
              onClick={() => setSelectedCategory("all")}
              className="bg-soft-purple-500 hover:bg-soft-purple-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
            >
              View All Videos
            </button>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🎥 Want to See Your Toy Featured?
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Have a toy you'd like us to review or demonstrate? Contact us and
              we might feature it in our next video!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleSuggestToy}
                className="bg-soft-purple-500 hover:bg-soft-purple-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                Suggest a Toy
              </button>
              <button
                onClick={handleSubscribeClick}
                className="bg-soft-blue-500 hover:bg-soft-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                Subscribe to Updates
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideosPage;
